import React from 'react'
import {
  Body,
  Grid,
  HorizontalSpacer,
  PageSection as Section,
  Price,
  VisuallyHide,
} from '@superhi/design'
import { useQuery } from '@apollo/client'

import { RECURRING_INTERVAL, useGetPrice } from '../../utils'

import Table from './components/Table'
import Wilson from './components/Wilson'
import { WilsonMissing } from './styles'
import { PRICES } from './queries'

import { API } from 'src/api'

const DATA: React.ComponentProps<typeof Table> = {
  columns: [
    {
      heading: 'Free',
    },
    {
      heading: 'Membership',
    },
  ],
  rows: [
    {
      heading:
        'Access to free content and tutorials, and to teaser content within paid collections',
      cells: [
        <HorizontalSpacer key={1} amount={{ small: 8, medium: 8, large: 8 }}>
          <span aria-hidden="true">
            <Wilson />{' '}
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </HorizontalSpacer>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'View on all devices, whether you use a desktop computer, tablet, or phone',
      cells: [
        <>
          <span aria-hidden="true">
            <Wilson key={1} />
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'Certificates of completion and proof of your achievements to share and celebrate',
      cells: [
        <>
          <span aria-hidden="true">
            <Wilson key={1} />
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading:
        'Helpful tools like our editor, books, and guides to create your own websites, and publish them online',
      cells: [
        <>
          <span aria-hidden="true">
            <Wilson key={1} />
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'Human support, with your requests handled by our friendly customer support team',
      cells: [
        <>
          <span aria-hidden="true">
            <Wilson key={1} />
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'Join our active, inclusive community of thousands of students on Discord',
      cells: [
        <>
          <span aria-hidden="true">
            <Wilson key={1} />
          </span>
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading:
        'Get in-depth guidance from content creators and our team, from technical help to hands-on tips and tricks',
      cells: [
        <>
          <span aria-hidden="true">
            <WilsonMissing key={1} />
          </span>
          <VisuallyHide>Not included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'Workshops, tutorials, homework, and extra exercises',
      cells: [
        <>
          <span aria-hidden="true">
            <WilsonMissing key={1} />
          </span>
          <VisuallyHide>Not included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading:
        'Thousands of dollars worth of content from the best creators, with more coming every month ',
      cells: [
        <>
          <span aria-hidden="true">
            <WilsonMissing key={1} />
          </span>
          <VisuallyHide>Not included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading:
        'Get the most of your membership with our personal recommendations and individual learning paths',
      cells: [
        <>
          <span aria-hidden="true">
            <WilsonMissing key={1} />
          </span>
          <VisuallyHide>Not included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
    {
      heading: 'See what’s coming up next and help shape future courses with early access',
      cells: [
        <>
          <span aria-hidden="true">
            <WilsonMissing key={1} />
          </span>
          <VisuallyHide>Not included</VisuallyHide>{' '}
        </>,
        <>
          <Wilson key={2} />
          <VisuallyHide>Included</VisuallyHide>{' '}
        </>,
      ],
    },
  ],
}

const Features: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data } = useQuery<API.MembershipProduct_Prices>(PRICES, {
    ssr: false,
  })
  const price = useGetPrice()

  const tableDataRows = React.useMemo(() => {
    return [
      {
        heading: 'Cost',
        cells: [
          <>
            <Body level="3">$0</Body>
          </>,
          <>
            {price && (
              <>
                <Body level="3" inline>
                  {price.display}
                  {price.recurring ? `/${RECURRING_INTERVAL[price.recurring.interval]}` : ''}
                </Body>
                {price.nextPriceIsDifferent && price.userDiscount && (
                  <Body level="3" color="GREY_70">
                    <Price currencyCode={price.currencyCode}>{price.nextPrice}</Price>
                    <span> after</span> {price.userDiscount.durationTextTone}
                  </Body>
                )}
              </>
            )}
          </>,
        ],
      },
      ...DATA.rows,
    ]
  }, [data, price])

  return (
    <Section
      title="The full lowdown"
      subtitle="Here's what you get when you sign up for a SuperHi Unlimited membership:"
      subtitleMaxWidth={40}
      headingLevel={2}
    >
      <Grid>
        <Grid.Cell span={{ small: 'full', medium: 'full', large: [2, 11] }}>
          <Table columns={DATA.columns} rows={tableDataRows} />
        </Grid.Cell>
      </Grid>
    </Section>
  )
}

export default Features
